import React, {useEffect, useMemo, useState} from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import {colors} from "nxtconstants";
import {RangeSlider} from "components/ClientHome/CalculatorComponents";
import {
  calculateSip,
  DEFAULT_INPUT,
  findClosestStepValue,
  INPUT_LIMITS,
  investmentSteps
} from "./CalculatorLogic";
import {
  ChartHoverInfo,
  ChartLabel,
  FrequencySelector,
  MobileTabToggle,
  SummaryCards,
  ViewToggle,
  YearlyTable
} from "./CalculatorComponents";
import {
  CalculatorSection,
  CalculatorWrapper,
  ChartContainer,
  ControlPanel,
  PageTitle,
  ResultsPanel
} from "./CalculatorStyle";

const CURRENT_YEAR = new Date().getFullYear();

const SipAndStepUpSipCalculator = () => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(
    DEFAULT_INPUT.monthlyInvestment
  );
  const [durationYears, setDurationYears] = useState(DEFAULT_INPUT.durationYears);
  const [annualReturn, setAnnualReturn] = useState(DEFAULT_INPUT.annualReturn);
  const [stepUpPercent, setStepUpPercent] = useState(DEFAULT_INPUT.stepUpPercent);
  const [frequency, setFrequency] = useState(DEFAULT_INPUT.frequency);
  const [activeView, setActiveView] = useState("graph");
  const [mobileTab, setMobileTab] = useState("input");
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setChartKey((previous) => previous + 1);
      }, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const result = useMemo(
    () =>
      calculateSip({
        monthlyInvestment,
        durationYears,
        annualReturn,
        stepUpPercent,
        frequency
      }),
    [monthlyInvestment, durationYears, annualReturn, stepUpPercent, frequency]
  );

  const chartData = useMemo(
    () =>
      result.yearlyData.map((row) => ({
        year: `Year ${row.year}`,
        Investment: row.totalInvested,
        Returns: row.gain,
        FutureValue: row.portfolioValue
      })),
    [result.yearlyData]
  );

  return (
    <CalculatorSection>
      <PageTitle>SIP &amp; Step-up SIP Calculator</PageTitle>

      <MobileTabToggle activeTab={mobileTab} onChange={setMobileTab} />

      <CalculatorWrapper>
        <ControlPanel $hidden={mobileTab === "result"}>
          <RangeSlider
            label="Monthly Investment"
            value={monthlyInvestment}
            onChange={setMonthlyInvestment}
            min={investmentSteps[0]}
            max={investmentSteps[investmentSteps.length - 1]}
            prefix="₹"
            findClosestStepValue={findClosestStepValue}
          />

          <RangeSlider
            label="Investment Period"
            value={durationYears}
            onChange={setDurationYears}
            min={INPUT_LIMITS.durationYears.min}
            max={INPUT_LIMITS.durationYears.max}
            step={1}
            suffix=" Years"
          />

          <RangeSlider
            label="Expected Rate of Return"
            value={annualReturn}
            onChange={setAnnualReturn}
            min={INPUT_LIMITS.annualReturn.min}
            max={INPUT_LIMITS.annualReturn.max}
            step={INPUT_LIMITS.annualReturn.step}
            suffix="%"
          />

          <RangeSlider
            label="Step-up"
            value={stepUpPercent}
            onChange={(value) => setStepUpPercent(Math.round(value))}
            min={INPUT_LIMITS.stepUpPercent.min}
            max={INPUT_LIMITS.stepUpPercent.max}
            step={INPUT_LIMITS.stepUpPercent.step}
            suffix="%"
          />

          <FrequencySelector value={frequency} onChange={setFrequency} />
        </ControlPanel>

        <ResultsPanel $hidden={mobileTab === "input"}>
          <SummaryCards
            futureValue={result.futureValue}
            gain={result.gain}
            totalInvested={result.totalInvested}
          />

          <ViewToggle activeView={activeView} onChange={setActiveView} />

          {activeView === "graph" ? (
            <ChartContainer>
              <ResponsiveContainer width="100%" height={400} key={chartKey}>
                <BarChart
                  barCategoryGap="10%"
                  barGap={2}
                  data={chartData}
                  margin={{top: 10, right: 8, left: 0, bottom: 30}}
                >
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    axisLine={false}
                    dataKey="year"
                    tick={{fontSize: 12, fill: "#9ca3af"}}
                    tickLine={false}
                  />
                  <YAxis
                    axisLine={false}
                    domain={[0, "dataMax + 20"]}
                    tick={{fontSize: 11, fill: "#9ca3af"}}
                    tickCount={5}
                    tickFormatter={(value) => {
                      if (value >= 10000000) {
                        return `₹${(value / 10000000).toFixed(1)}Cr`;
                      }
                      if (value >= 100000) {
                        return `₹${(value / 100000).toFixed(1)}L`;
                      }
                      if (value >= 1000) {
                        return `₹${(value / 1000).toFixed(0)}k`;
                      }
                      return `₹${value}`;
                    }}
                    tickLine={false}
                    width={56}
                  />
                  <Tooltip
                    content={<ChartHoverInfo />}
                    cursor={colors.chartCursor}
                  />
                  <Legend
                    align="center"
                    content={<ChartLabel />}
                    layout="horizontal"
                    verticalAlign="bottom"
                  />
                  <Bar
                    activeBar={{fill: colors.primary, opacity: 1}}
                    dataKey="Investment"
                    fill={colors.primary}
                    name="Invested Amount"
                    radius={[0, 0, 2, 2]}
                    stackId="a"
                  />
                  <Bar
                    activeBar={{
                      fill: colors.chartAccent,
                      opacity: 1,
                      stroke: "#ffffff",
                      strokeWidth: 1
                    }}
                    dataKey="Returns"
                    fill={colors.chartAccent}
                    name="Estimated Return"
                    radius={[3, 3, 0, 0]}
                    stackId="a"
                    stroke="#ffffff"
                    strokeWidth={1}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          ) : (
            <YearlyTable startYear={CURRENT_YEAR} yearlyData={result.yearlyData} />
          )}
        </ResultsPanel>
      </CalculatorWrapper>
    </CalculatorSection>
  );
};

export default SipAndStepUpSipCalculator;
